import { AfterViewInit, Component, EventEmitter, Output, effect, inject, ViewChild } from '@angular/core';
import {
  ExcelStyle,
  GridOptions,
  ITooltipParams,
} from 'ag-grid-community';
import { 
  AgGridAngular,
  AgGridModule 
} from 'ag-grid-angular';
import { ViewIconRendererComponent } from '../../../ag-grid-components/view-icon-renderer/view-icon-renderer.component';
import { AgPaginatorComponent } from '../../../ag-grid-components/ag-paginator/ag-paginator.component';
import { FileDataStore } from '../../../../store/file-data.store';
import { DatePipe } from '@angular/common';
import { AgExportButtonComponent, IAgExportButton } from '../../../ag-grid-components/ag-export-button/ag-export-button.component';

export interface FileDataTableRow {
  id: number;
  uploadDate: Date;
  payerAlias: string;
  uploadedBy: string;
  fileName: string;
  status: string;
  fileType: string;
  minDos: Date | null;
  maxDos: Date | null;
}

@Component({
    selector: 'app-file-upload-table',
    imports: [AgGridModule],
    templateUrl: './file-upload-table.component.html',
    styleUrl: './file-upload-table.component.scss'
})
export class FileUploadTableComponent implements AfterViewInit {
  readonly store = inject(FileDataStore);
  readonly datePipe = inject(DatePipe);
  exportTooltip = 'Export CMS Files';
  excludeColumnIdsOnExport = ['action'];
  excelStyles: ExcelStyle[] = [
    {
      id: 'header',
      alignment: {
        horizontal: 'Center',
      },
      borders: {
        borderTop: {
          color: '#b3b3b3',
          lineStyle: 'Continuous',
          weight: 2,
        },
        borderBottom: {
          color: '#b3b3b3',
          lineStyle: 'Continuous',
          weight: 2,
        },
        borderLeft: {
          color: '#b3b3b3',
          lineStyle: 'Continuous',
          weight: 2,
        },
        borderRight: {
          color: '#b3b3b3',
          lineStyle: 'Continuous',
          weight: 2,
        },
      },
      interior: {
        color: '#c5d7ed',
        pattern: 'Solid',
      },
    },
  ];

  @Output() export: EventEmitter<AgGridAngular> = new EventEmitter<AgGridAngular>();
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  gridOptions: GridOptions = {
    defaultColDef: {
      suppressMovable: true,
      suppressHeaderMenuButton: true
    },
    columnDefs: [
      { 
        field: "uploadDate", 
        headerName: "Upload Date", 
        flex: 1,
        valueFormatter: (params) => { return this.datePipe.transform(params.data?.uploadDate, "MM/dd/yyyy") ?? ""; }
      },
      {
        field: "fileType",
        headerName: "File Type",
        flex: 1,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        valueFormatter: (params) => { return params.data?.fileType ?? "" }
      },
      { field: "payerAlias", headerName: "Payer Alias", flex: 1, suppressMovable: true, suppressHeaderMenuButton: true },
      { field: "uploadedBy", headerName: "Uploaded By", flex: 1, suppressMovable: true, suppressHeaderMenuButton: true },
      {
        field: "fileName",
        headerName: "File Name",
        flex: 2,
        tooltip: (params: ITooltipParams) => params.value as string,
      },
      {
        field: "minDos",
        headerName: "Min DOS",
        flex: 1,
        valueFormatter: (params) => { return this.datePipe.transform(params.data?.minDos, "MM/dd/yyyy") ?? ""; }
      },
      {
        field: "maxDos",
        headerName: "Max DOS",
        flex: 1,
        valueFormatter: (params) => { return this.datePipe.transform(params.data?.maxDos, "MM/dd/yyyy") ?? ""; }
      },
      {
        field: "status",
        headerName: "File Status",
        flex: 1,
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 84,
        maxWidth: 84,
        cellRenderer: "viewRenderer",
        sortable: false,
      },
    ],
    statusBar: {
      statusPanels: [
        {
          statusPanel: AgPaginatorComponent,
          key: "ag-paginator",
        },
        {
          statusPanel: AgExportButtonComponent,
          key: 'export-btn'
        },
      ],
    },
    paginationPageSize: 25,
    paginationPageSizeSelector: [25, 50, 100, 150, 300],
  };

  gridComponents = {
    viewRenderer: ViewIconRendererComponent,
  };

  gridContext = { componentParent: this };

  constructor() {
    effect(() => {
      this.syncExportDisabledState();
    });
  }

  ngAfterViewInit() {
    this.agGrid?.api?.getStatusPanel<IAgExportButton>('export-btn')?.updateExportActive(true);
    this.syncExportDisabledState();
  }

  onExportClick() {
    this.export.emit(this.agGrid); 
  }

  onPaginatorPageSizeChange(pageSize: number) {
    this.gridOptions.paginationPageSize = pageSize;
  }

  private syncExportDisabledState() {
    const hasRows = this.store.tableData().length > 0;
    this.agGrid?.api?.getStatusPanel<IAgExportButton>('export-btn')?.updateExportDisabled(!hasRows);
  }
}
